import { Injectable } from '@angular/core';
import { ApiService } from '@core/services/api.service';
import { StateService } from '@core/services/state.service';
import { User } from 'app/auth/models';
import { AuthenticationService } from 'app/auth/service';
import {
  BehaviorSubject,
  of,
  Observable,
  throwError,
  combineLatest,
  forkJoin,
  Subject,
} from 'rxjs';
import {
  switchMap,
  take,
  tap,
  catchError,
  map,
  withLatestFrom,
} from 'rxjs/operators';
import { Column, Dolgozo, OnelszamoloEgyseg, Raktar } from '../models/torzsek';
import { addHours, findByProp, isJSONString, parseJSON, stringifyIfJSON } from '../utils';
import { FelhasznaloiCsoportokService } from './felhasznaloi-csoportok.service';
import { environment } from 'environments/environment';
import { BeosztasokService } from './beosztasok.service';
import { OnelszamoloEgysegekService } from './onelszamolo-egysegek.service';
import { PassThrough } from 'stream';
import { isNumber } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class MunkatarsakService {
  private readonly apiEndpoint = 'munkatarsak';
  private readonly columnsEndpoint = 'munkatarsak/columns';
  private readonly dolgozoUpdateEndpoint = 'service/frissiteslib/dolgozo_frissites';
  private readonly dolgozo_lista = 'service/szabadsaglib/dolgozo_lista';
  private readonly jelszo_mod = 'service/torzslib/jelszo_modositas';
  private readonly nyomtatasEndpoint = 'jelenleti';
  private readonly checkJelszoEndpoint = 'service/torzslib/jelszo_ellenorzes';
  private readonly jelszoModErp = 'service/torzslib/jelszo_modositas_erp';
  private readonly sendEndpoint = 'service/emailkuldes/hasznalati_szabadsag';

  private readonly _data = new BehaviorSubject<Dolgozo[]>(null);
  private readonly _selectedData = new BehaviorSubject<Dolgozo>(null);
  public jelenletiPrint = new BehaviorSubject<any>(null);
  public jogviszonyChange = new BehaviorSubject<any>(null);

  public scrollToTop = new Subject<boolean>();

  private lastFetch: Date = new Date();

  private state$ = combineLatest([
    this._data,
    this.fCsoportokService.data$,
    this.oeService.initData$
  ]).pipe(
    switchMap(([state, fCsoportok, oek]) =>
      state
        ? of(this.mapDolgozok(state, fCsoportok, oek))
        : this.fetchData()
    )
  );

  // public data$ = combineLatest([
  //   this.state$,
  //   this.authService.currentUser$,
  // ]).pipe(
  //   map(([data, user]) =>
  //     data.filter((e: any) => e.ceg_id == null ? true : user.oe == e.ceg_id).sort((a, b) => a.teljes_nev.localeCompare(b.teljes_nev))
  //   )
  // );

  public data$ = combineLatest([
  this.state$,
  this.authService.currentUser$,
]).pipe(
  map(([data, user]) =>
    data
      .filter((e: any) => e.ceg_id == null ? true : user?.oe == e?.ceg_id)
      .sort((a, b) => {
        const nameCompare = a.teljes_nev.localeCompare(b.teljes_nev);
        if (nameCompare !== 0) return nameCompare;
        return new Date(a.munkaviszony_kezdete).getTime() - new Date(b.munkaviszony_kezdete).getTime();
      })
  )
);


  constructor(
    private api: ApiService,
    private fCsoportokService: FelhasznaloiCsoportokService,
    private beosztasokService: BeosztasokService,
    private stateService: StateService,
    private authService: AuthenticationService,
    private oeService: OnelszamoloEgysegekService
    //private telephelyekService: TelephelyekService
  ) {}

  get columns$(): Observable<Column[]> {
    return this.getColumns();
  }

  get initData$(): Observable<Dolgozo[]> {
    return this._data.pipe(
      take(1),
      switchMap((state: any[]) =>
        state
          ? forkJoin([
              of(state),
              this.fCsoportokService.initData$,
              this.oeService.initData$
              //this.telephelyekService.initDataTelephely$
            ]).pipe(
              map(([dolgozok, fCsoportok, oek]) =>
                this.mapDolgozok(dolgozok, fCsoportok, oek)
              )
            )
          : this.fetchData()
      ),
      withLatestFrom(this.authService.currentUser$),
      map(([data, user]) =>
        data.filter((e: any) => e.ceg_id == null ? true : e?.ceg_id === user?.oe).sort((a, b) => {
        const nameCompare = a.teljes_nev.localeCompare(b.teljes_nev);
        if (nameCompare !== 0) return nameCompare;
        return new Date(a.munkaviszony_kezdete).getTime() - new Date(b.munkaviszony_kezdete).getTime();
      })
      )
    );
  }

  get initDataAll$(): Observable<Dolgozo[]> {
    return this._data.pipe(
      take(1),
      switchMap((state: any[]) =>
        state
          ? forkJoin([
              of(state),
              this.fCsoportokService.initData$,
              this.oeService.initData$
              //this.telephelyekService.initDataTelephely$
            ]).pipe(
              map(([dolgozok, fCsoportok, oek]) =>
                this.mapDolgozok(dolgozok, fCsoportok, oek)
              )
            )
          : this.fetchData()
      ),
      withLatestFrom(this.authService.currentUser$),
      map(([data, user]) =>
        data
      )
    );
  }

  get vezetok$(): Observable<Dolgozo[]> {
    return this._data.pipe(
      take(1),
      switchMap((state: any[]) =>
        state
          ? forkJoin([
              of(state),
              this.fCsoportokService.initData$,
              this.beosztasokService.initData$,
              //this.telephelyekService.initDataTelephely$
            ]).pipe(
              map(([dolgozok, fCsoportok, beosztasok]) =>
                this.mapDolgozok(dolgozok, fCsoportok).filter((e: any) => findByProp(beosztasok, 'nev', e.beosztas)?.vezeto)
              )
            )
          : this.fetchDataVezetok()
      )
    );
  }

  add(data: any): Observable<any> {
    return this.api.post(this.apiEndpoint, this.toJSON(data)).pipe(
      tap((added: any) =>
        this.stateService.setStateOnAdd(this.fromJSON(added), this._data)
      ),
      catchError((err: any) => throwError(err))
    );
  }

  jelszoMod(data: any): Observable<any> {
    return this.api.postWithoutMsg(this.jelszo_mod, data).pipe(
      catchError((err: any) => throwError(err))
    );
  }

  jelszoModMunkatars(data: any): Observable<any> {
    return this.api.postWithoutMsg(this.jelszoModErp, data).pipe(
      catchError((err: any) => throwError(err))
    );
  }

  checkJelszo(data: any): Observable<any> {
    return this.api.postWithoutMsg(this.checkJelszoEndpoint, data).pipe(
      catchError((err: any) => throwError(err))
    );
  }

  delete(id: number): Observable<any> {
    return this.api.delete(this.apiEndpoint, id).pipe(
      tap((deleted: any) =>
        this.stateService.setStateOnDelete(deleted, this._data)
      ),
      catchError((err: any) => throwError(err))
    );
  }

  getById(id: string): Observable<any> {
    return this.data$.pipe(
      take(1),
      map((state) => {
        // Find the data
        const data = state.find((item: any) => item.id === +id) || null;

        // Update the data
        this._selectedData.next(data);

        // Return the data
        return data;
      }),
      switchMap((data: any | null) => {
        if (id === 'new') {
          return of(null);
        }

        if (!data) {
          return throwError('Could not found data with id of ' + id + '!');
        }

        return of(data);
      })
    );
  }

  update(data: any): Observable<any> {
    return this.api
      .put(`${this.apiEndpoint}/${data.id}`, this.toJSON(data))
      .pipe(
        map((updateRes: any) => this.fromJSON(updateRes)),
        tap((updated: any) => {
          // Update state
          this.stateService.setStateOnUpdate(updated, this._data);
        }),
        switchMap((updated: any) =>
          // Logged in user updated
          updated.id === this.authService.currentUserValue?.id
            ? forkJoin([
                this.authService.currentUser$.pipe(take(1)),
                this.fCsoportokService.initData$,
              ]).pipe(
                tap(([ user, fCsoportok]) => {
                  // Update user state
                  const updatedUser: User = {
                    ...user,
                    nev: `${updated.vezeteknev} ${updated.keresztnev}`,
                    raktar: updated.alapertelmezett_raktar_id,
                    felhasznalonev: updated.felhasznalonev,
                    felhasznaloi_csoport: {
                      id: updated.felhasznaloi_csoport_id,
                      nev: findByProp(
                        fCsoportok,
                        'id',
                        updated.felhasznaloi_csoport_id
                      )?.nev,
                    },
                    oek: updated.oek as number[],
                    raktarak: updated.raktarak as number[],
                  };

                  // Update local state and localStorage
                  this.authService.currentUser = updatedUser;
                }),
                map(() => {
                  return updated;
                })
              )
            : // Other update
              of(updated)
        ),
        catchError((err: any) => throwError(err))
      );
  }

  updateNoToastr(data: any): Observable<any> {
    return this.api
      .put(`${this.apiEndpoint}/${data.id}`, this.toJSON(data), 'disable')
      .pipe(
        map((updateRes: any) => this.fromJSON(updateRes)),
        tap((updated: any) => {
          // Update state
          this.stateService.setStateOnUpdate(updated, this._data);
        }),
        switchMap((updated: any) =>
          // Logged in user updated
          updated.id === this.authService.currentUserValue?.id
            ? forkJoin([
                this.authService.currentUser$.pipe(take(1)),
                this.fCsoportokService.initData$,
              ]).pipe(
                tap(([ user, fCsoportok]) => {
                  // Update user state
                  const updatedUser: User = {
                    ...user,
                    nev: `${updated.vezeteknev} ${updated.keresztnev}`,
                    raktar: updated.alapertelmezett_raktar_id,
                    felhasznalonev: updated.felhasznalonev,
                    felhasznaloi_csoport: {
                      id: updated.felhasznaloi_csoport_id,
                      nev: findByProp(
                        fCsoportok,
                        'id',
                        updated.felhasznaloi_csoport_id
                      )?.nev,
                    },
                    oek: updated.oek as number[],
                    raktarak: updated.raktarak as number[],
                  };

                  // Update local state and localStorage
                  this.authService.currentUser = updatedUser;
                }),
                map(() => {
                  return updated;
                })
              )
            : // Other update
              of(updated)
        ),
        catchError((err: any) => throwError(err))
      );
  }

  fetchAndMapDataById(id: any): Observable<any[]> {
    return forkJoin([
      this.api.get(`${this.apiEndpoint}/${id}`).pipe(
        map((munkatars: any) => this.fromJSON(munkatars)),
        catchError((err: any) => {
          console.log(err);
          return of([]);
        })
      ),
      this.fCsoportokService.initData$,
      this.oeService.initData$
      //this.telephelyekService.initDataTelephely$
    ]).pipe(
      map(([dolgozo, fCsoportok, oek]) => {
        return this.mapDolgozo(dolgozo, fCsoportok, oek)
      })
    );
  }

  fetchData(): Observable<any[]> {
    this.lastFetch = new Date();
    return forkJoin([
      this.api.get(this.apiEndpoint).pipe(
        map((dolgozok: any[]) =>
          dolgozok.map((d: any) => this.fromJSON(d))
        ),
        tap((data: any[]) => {
          this._data.next(data)
        }),
        catchError((err: any) => {
          console.log(err);
          return of([]);
        })
      ),
      this.fCsoportokService.initData$,
      this.oeService.initData$
      //this.telephelyekService.initDataTelephely$
    ]).pipe(
      map(([dolgozok, fCsoportok, oek]) => {
        return this.mapDolgozok(dolgozok, fCsoportok, oek)
      })
    );
  }

  private fetchDataVezetok(): Observable<any[]> {
    return forkJoin([
      this.api.get(this.apiEndpoint).pipe(
        map((dolgozok: any[]) =>
          dolgozok.map((d: any) => this.fromJSON(d))
        ),
        catchError((err: any) => {
          console.log(err);
          return of([]);
        })
      ),
      this.fCsoportokService.initData$,
      this.beosztasokService.initData$,
      //this.telephelyekService.initDataTelephely$
    ]).pipe(
      map(([dolgozok, fCsoportok, beosztasok]) =>
        this.mapDolgozok(dolgozok, fCsoportok).filter((e: any) => findByProp(beosztasok, 'nev', e.beosztas)?.vezeto)
      )
    );
  }

  fetchDolgozoLista(): Observable<any[]> {
    return forkJoin([
      this.api.get(this.dolgozo_lista).pipe(
        map((dolgozok: any[]) =>
          dolgozok.map((d: any) => this.fromJSON(d))
        ),
        catchError((err: any) => {
          console.log(err);
          return of([]);
        })
      ),
      this.fCsoportokService.initData$,
      this.beosztasokService.initData$,
      //this.telephelyekService.initDataTelephely$
    ]).pipe(
      map(([dolgozok, fCsoportok, beosztasok]) =>
        this.mapDolgozok(dolgozok, fCsoportok)
      )
    );
  }

  sendUtmutato(munkatars_id: any): Observable<any> {
    return this.api.post(`${this.sendEndpoint}/${munkatars_id}`, '', 'Sikeres küldés').pipe(
      catchError((err: any) => throwError(err))
    );
  }

  generateFileToMunkatars(nyomtatvany: any, munkatarsId: number): Observable<any> {
    return this.generateFile(nyomtatvany.id, munkatarsId).pipe(
      tap((generatedFile: any) => {
        const element = findByProp(this._data.value, 'id', +munkatarsId);

        const updatedElement = {
          ...element,
          fajlok: [...element.fajlok, generatedFile],
        };

        this.stateService.setStateOnUpdate(updatedElement, this._data);
      })
    );
  }


  generateFile(
    nyomtatvanyId: number,
    ugyfelMunkatarsId: number
  ): Observable<any> {
    return this.api
      .get(
        `${environment.printApi}/${nyomtatvanyId}/${ugyfelMunkatarsId}`
      )
      .pipe(
        tap((data: any) => {
          window.open(`${environment.filesEndpoint}/${data.utvonal}`, '_blank');
        }),
        catchError((err: any) => {
          console.log(err);
          return of([]);
        })
      );
  }

  getColumnsSelect(): Observable<Column[]> {
    return this.api.get(this.columnsEndpoint).pipe(
      map((columnsRes: Column[]) => {
        const alapertelmezettOe: Column = {
          name: 'alapertelmezett_oe',
          nullable: true,
        };

        const alapertelmezettRaktar: Column = {
          name: 'alapertelmezett_raktar',
          nullable: true,
        };

        const telephelyNev: Column = {
          name: 'telephely_nev',
          nullable: true,
          title: 'Telephely'
        };

        const columns = [
          ...columnsRes,
          telephelyNev,
          alapertelmezettOe,
          alapertelmezettRaktar,
          {
            name: 'igenybe_vett_kedv',
            nullable: true,
            title: 'Igénybe vett kedvezmény'
          },
          {
            name: 'elso_hazasok_kedv',
            nullable: true,
            title: 'Első házasok kedvezménye'
          },
          {
            name: 'sulyos_fogyi',
            nullable: true,
            title: 'Súlyos fogyatékosság'
          },
          {
            name: 'hazastars_vezeteknev',
            nullable: true,
            title: 'Házastárs vezetéknév'
          },
          {
            name: 'hazastars_keresztnev',
            nullable: true,
            title: 'Házastárs keresztnév'
          },
          {
            name: 'hazastars_adoazonosito',
            nullable: true,
            title: 'Házastárs adóazonosító'
          },
          {
            name: 'eltartottak',
            nullable: true,
            title: 'Eltartottak'
          },
          {
            name: 'eltartottak_szama',
            nullable: true,
            title: 'Eltartottak száma'
          },
          {
            name: 'iskolai_vegzettsegek',
            nullable: true,
            title: 'Iskolai végzettségek'
          },
          {
            name: 'vegzettsegek',
            nullable: true,
            title: 'Munkatapasztalat'
          },
          {
            name: 'nyelvtudas',
            nullable: true,
            title: 'Nyelvtudás'
          },
          {
            name: 'jogositvanyok',
            nullable: true,
            title: 'Jogosítványok'
          }
        ];

        const shownColumns = [
          'id',
          'ceg_id',
          'dolgozoi_csoport_id',
          'neme',
          'szemelyigazolvany_szama',
          'ado_azonosito',
          'tb_azonosito',
          'tamogatas_szama',
          'tamogatas_lejarta',
          'nyugdij_kod',
          'nyugdij_folyositasi_torzsszam',
          'anyja_neve',
          'szuletesi_vezeteknev',
          'szuletesi_keresztnev',
          'szuletesi_helye',
          'szuletesi_datum',
          'cimek',
          'allampolgarsag',
          'probaido_kezdete',
          'munkaviszony_kezdete',
          'munkaviszony_vege',
          'megszunes_modja',
          'munkaviszony_megszunesenek_oka',
          'hatarozott_munkaido_vege',
          'munkaido_keret',
          'munkakor',
          'beosztas',
          'bejaras',
          'evi_rendes_fiz_szab_keret',
          'evi_beteg_szab_keret',
          'kivett_szabadsag',
          'eves_szabadsag_ora',
          'kivett_szabadsag_ora',
          'athozott_szabadsag',
          'bankszamla',
          'szepkartya',
          'munkaviszony_jellege',
          'havi_ber',
          'ora_ber',
          'napi_megtett_kilometer',
          'ft_km',
          'napi_munkaido',
          'igenybe_vett_kedv',
          'elso_hazasok_kedv',
          'sulyos_fogyi',
          'hazastars_vezeteknev',
          'hazastars_keresztnev',
          'hazastars_adoazonosito',
          'eltartottak',
          'eltartottak_szama',
          'fizetes_bankszamlara',
          'probaido_vege',
          'feor',
          'iskolai_vegzettsegek',
          'nyelvtudas',
          'jogositvanyok',
          'telefonszam',
          'munkaviszony_tipusa',
          'jogviszony',
          'felettes_id',
          'jogkorgyakorlo_id',
          'egeszsegugyi_adatok',
          'targyi_eszkozok',
          'megjegyzesek',
          'levonasok',
          'email',
          'vezeteknev',
          'keresztnev',
          'beosztas',
          'utolso_bejelentkezes',
        ];

        const displayedColumns = columns.filter((c: Column) =>
          shownColumns.includes(c.name) ? 
          c.name == 'havi_ber' ? {...c, type: 'currency', suffix: 'Ft'} : 
          c.name == 'heti_munkaido' ? {...c, type: 'text', suffix: 'óra'} : 
          c.name == 'munkaviszony_kezdete' ? {...c, type: 'text'} : 
          c.name == 'munkaltato' ? {...c, type: 'text'} : 
          c.name == 'jogviszony' ? {...c, type: 'text'} : 
          c.name == 'munkaviszony_vege' ? {...c, type: 'text'} : c
          : { ...c, hidden: true }
        );

        return displayedColumns;
      }),
      catchError((err: any) => {
        console.log(err);
        return of([]);
      })
    );
  }

  private getColumns(): Observable<Column[]> {
    return this.api.get(this.columnsEndpoint).pipe(
      map((columnsRes: Column[]) => {
        const jelszoVisszallitas: Column = {
          name: 'jelszo_visszaallitas',
          nullable: true,
          hidden: true,
        };

        const ujJelszo: Column = {
          name: 'uj_jelszo',
          nullable: true,
          hidden: true,
        };

        const alapertelmezettOe: Column = {
          name: 'alapertelmezett_oe',
          nullable: true,
        };

        const alapertelmezettRaktar: Column = {
          name: 'alapertelmezett_raktar',
          nullable: true,
        };

        const cegNev: Column = {
          name: 'ceg_nev',
          nullable: true,
          title: 'Munkáltató'
        };

        const columns = [
          ...columnsRes,
          cegNev,
          alapertelmezettOe,
          alapertelmezettRaktar,
          jelszoVisszallitas,
          ujJelszo,
        ];

        const shownColumns = [
          'id',
          'vezeteknev',
          'keresztnev',
          'munkakor',
          'munkaviszony_kezdete',
          'munkaviszony_vege',
          'heti_munkaido',
          'havi_ber',
          'ceg_nev',
          'jogviszony'
        ];

        const displayedColumns = columns.map((c: Column) =>
          shownColumns.includes(c.name) ? 
          c.name == 'havi_ber' ? {...c, type: 'currency', suffix: 'Ft'} : 
          c.name == 'heti_munkaido' ? {...c, type: 'text', suffix: 'óra'} : 
          c.name == 'munkaviszony_kezdete' ? {...c, type: 'text'} : 
          c.name == 'munkaviszony_vege' ? {...c, type: 'text'} : c
          : { ...c, hidden: true }
        );

        return displayedColumns;
      }),
      catchError((err: any) => {
        console.log(err);
        return of([]);
      })
    );
  }

  private mapDolgozok(
    dolgozok: any[],
    fCsoportok: any[],
    oek?: any[]
  ): any[] {
    return dolgozok.map((m: any) => ({
      ...m,
      telefonszam: parseJSON(m.telefonszam) ? parseJSON(m.telefonszam)[0] : null,
      felhasznaloi_csoport: findByProp(
        fCsoportok,
        'id',
        m.felhasznaloi_csoport_id
      )?.nev,
      cimek: parseJSON(m.cimek),
      jogviszony: parseJSON(m.jogviszony),
      teljes_nev: m.vezeteknev + ' ' + m.keresztnev,
      ceg_nev: findByProp(oek, 'id', m.ceg_id)?.megnevezes,
      elozmeny: parseJSON(m.elozmeny),
      munka_helye: Array.isArray(parseJSON(m.munka_helye)) ? parseJSON(m.munka_helye) : [parseJSON(m.munka_helye)],
      palyazatok: parseJSON(m.palyaztok),
      fizetes_bankszamlara: m.fizetes_bankszamlara == 'bankszámla' ? 'átutalás' : m.fizetes_bankszamlara,
      szervezeti_egyseg: +m.szervezeti_egyseg,
      ceges_email: isJSONString(m.ceges_email) ? parseJSON(m.ceges_email) : [m.ceges_email],
      ceges_telefonszam: m.ceges_telefonszam ? (!isNaN(m.ceges_telefonszam) ? [this.mapTelefonszam(m.ceges_telefonszam as string)] : parseJSON(m.ceges_telefonszam)) : []
    }));
  }

  private mapDolgozo(
    dolgozo: any,
    fCsoportok: any[],
    oek?: any[]
  ): any[] {
    const munkaido_kezd_ora = dolgozo.munkaido_kezd?.split(':')[0] || 8;
    const munkaido_kezd_perc = dolgozo.munkaido_kezd?.split(':')[1] || 0;
    const munkaido_vege_ora = dolgozo.munkaido_vege?.split(':')[0] || 16;
    const munkaido_vege_perc = dolgozo.munkaido_vege?.split(':')[1] || 0;

    const munkaido_kezd = new Date(new Date().setHours(munkaido_kezd_ora, munkaido_kezd_perc, 0, 0));
    const munkaido_vege = new Date(new Date().setHours(munkaido_vege_ora, munkaido_vege_perc, 0, 0));

    return {
      ...dolgozo,
      telefonszam: parseJSON(dolgozo.telefonszam) ? parseJSON(dolgozo.telefonszam) : null,
      felhasznaloi_csoport: findByProp(
        fCsoportok,
        'id',
        dolgozo.felhasznaloi_csoport_id
      )?.nev,
      cimek: parseJSON(dolgozo.cimek),
      jogviszony: parseJSON(dolgozo.jogviszony),
      teljes_nev: dolgozo.vezeteknev + ' ' + dolgozo.keresztnev,
      ceg_nev: findByProp(oek, 'id', dolgozo.ceg_id)?.megnevezes,
      elozmeny: parseJSON(dolgozo.elozmeny),
      munkaido_kezd: dolgozo.munkaido_kezd ? munkaido_kezd : null,
      munkaido_vege: dolgozo.munkaido_vege ? munkaido_vege : null,
      // munkaido_kezd: dolgozo.munkaido_kezd ? dolgozo.munkaido_kezd?.split(':')[0] + ':' + dolgozo.munkaido_kezd?.split(':')[1] : null,
      // munkaido_vege: dolgozo.munkaido_vege ?  dolgozo.munkaido_vege?.split(':')[0] + ':' + dolgozo.munkaido_vege?.split(':')[1] : null,
      munka_helye: Array.isArray(parseJSON(dolgozo.munka_helye)) ? parseJSON(dolgozo.munka_helye) : [parseJSON(dolgozo.munka_helye)],
      palyazatok: parseJSON(dolgozo.palyaztok),
      fizetes_bankszamlara: dolgozo.fizetes_bankszamlara == 'bankszámla' ? 'átutalás' : dolgozo.fizetes_bankszamlara,
      szervezeti_egyseg: +dolgozo.szervezeti_egyseg,
      ceges_email: isJSONString(dolgozo.ceges_email) ? parseJSON(dolgozo.ceges_email) : [dolgozo.ceges_email],
      ceges_telefonszam: dolgozo.ceges_telefonszam ? (!isNaN(dolgozo.ceges_telefonszam) ? [this.mapTelefonszam(dolgozo.ceges_telefonszam as string)] : parseJSON(dolgozo.ceges_telefonszam)) : [],
      munkanapok_efo: dolgozo.munkanapok_efo ? parseJSON(dolgozo.munkanapok_efo) : [],
      nyugdij_kod: dolgozo.nyugdij_kod != null ? dolgozo.nyugdij_kod.toString() : null
    };
  }

  private fromJSON(dolgozo: any): any {
    return {
      ...dolgozo,
      elozmeny: parseJSON(dolgozo.elozmeny),
    };
  }

  mapTelefonszam(telszam: string): string {
    if(!telszam) return null;

    const temp = telszam.replace(/\D/g, '').match(/(\d{0,2})(\d{0,2})(\d{0,3})(\d{0,4})/);
    return `+${temp[1]}-${temp[2]}-${temp[3]}-${temp[4]}`;
  }

  private toJSON(dolgozo: any): any {
    console.log('tojson', dolgozo)
    return {
      ...dolgozo,
      jogviszony: stringifyIfJSON(dolgozo.jogviszony),
      szuletesi_datum: dolgozo.szuletesi_datum ? addHours(dolgozo.szuletesi_datum, 3) : null,
      munkaviszony_kezdete: dolgozo.munkaviszony_kezdete ? addHours(dolgozo.munkaviszony_kezdete, 3) : null,
      munkaviszony_vege: dolgozo.munkaviszony_vege ? addHours(dolgozo.munkaviszony_vege, 3) : null,
      tamogatas_lejarta: dolgozo.tamogatas_lejarta ? addHours(dolgozo.tamogatas_lejarta, 3) : null,
      rehab_jogosultsag_kezdete: dolgozo.rehab_jogosultsag_kezdete ? addHours(dolgozo.rehab_jogosultsag_kezdete, 3) : null,
      rehab_jogosultsag_vege: dolgozo.rehab_jogosultsag_vege ? addHours(dolgozo.rehab_jogosultsag_vege, 3) : null,
      ceges_telefonszam: stringifyIfJSON(dolgozo.ceges_telefonszam),
      ceges_email: stringifyIfJSON(dolgozo.ceges_email),
      munkanapok_efo: stringifyIfJSON(dolgozo.munkanapok_efo),
      jelszo: dolgozo.jelszo ? dolgozo.jelszo : '-'
    };
  }

  generatePdf(payload: any): Observable<any> {
    return this.api
      .postBizonylat(
        `${environment.pdfGeneralas}/${this.nyomtatasEndpoint}`,
        payload
      )
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  getDolgozoChange(): any {
    this.lastFetch = new Date();
    const last = JSON.parse(localStorage.getItem('munkatarsak'))?.lastFetch;
    this.api.get(`${this.dolgozoUpdateEndpoint}/${last}`).pipe(
        // take(1),
        //tap((data: any[]) => this._data.next(data)),
        catchError((err: any) => {
          console.log(err);
          return of([]);
        })
    )
    .subscribe((valtozas: any) => {
      if(valtozas) {
        if(this._data.value) {

        }
      }
    }
    );
  }
}
